import { fetchLabModalPopups } from 'actions/SystemActions'
import { ModalStackIdleContext, TryShowModalContext } from 'contexts/ModalProvider'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useLabModalPopupsRequestContext } from 'hooks/useLabRouteRequestContext'
import { getFirstEligibleLabItemByRank } from 'lib/lab/rankedLabItems'
import Label from 'LuxKit/Label'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import type React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { recordModalPopupSeen, isModalPopupSuppressed } from 'storage/modalPopup'
import styled from 'styled-components'

type LabModalPopupStyle = 'light' | 'dark'

export const LAB_MODAL_POPUP_DARK_CLASS_NAME = 'lab-modal-popup-dark'

const StyledModal = styled(Modal)`
  [data-testid="lab-modal-popup-header"] [data-testid="lab-modal-popup-header-close-button"] {
    --button-color: var(--button-hover-color);
    --button-background-color: var(--button-hover-background-color);
    --button-border-color: var(--button-hover-border-color, transparent);
    --button-focus-color: var(--button-hover-color);
    --button-focus-background-color: var(--button-hover-background-color);
    --button-focus-border-color: var(--button-hover-border-color, transparent);
    --button-active-color: var(--button-hover-color);
    --button-active-background-color: var(--button-hover-background-color);
    --button-active-border-color: var(--button-hover-border-color, transparent);
  }

  &.${LAB_MODAL_POPUP_DARK_CLASS_NAME} {
    background-color: ${props => props.theme.palette.neutral.control.black100};
    color: ${props => props.theme.palette.neutral.surface.staticWhite};

    [data-testid="lab-modal-popup-header"],
    [data-testid="lab-modal-popup-footer"] {
      background-color: ${props => props.theme.palette.neutral.control.black100};
      color: ${props => props.theme.palette.neutral.surface.staticWhite};
    }

    [data-testid="lab-modal-popup-header"].is-overlay {
      background-color: transparent;
    }

    [data-testid="lab-modal-popup-footer-secondary-action"],
    [data-testid="lab-modal-popup-header"] button:not([data-testid="lab-modal-popup-header-close-button"]) {
      color: ${props => props.theme.palette.neutral.surface.staticWhite};
    }

    [data-testid="lab-modal-popup-footer-primary-action"] {
      --color-primary-bg: ${props => props.theme.palette.neutral.surface.staticWhite};
      --color-primary-fg: ${props => props.theme.palette.neutral.surface.lightContrast};
      --color-primary-hover: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
      --color-primary-active: ${props => props.theme.palette.neutral.surface.lightForeground};
    }

    [data-testid="lab-modal-popup-footer-secondary-action"]:hover {
      background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
      color: ${props => props.theme.palette.neutral.surface.staticWhite};
    }
  }
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const getModalPopupStyle = (popup: App.LabModalPopupResult): LabModalPopupStyle => {
  return popup.style === 'dark' ? 'dark' : 'light'
}

function getRenderableModalPopup(modalPopups: App.LabModalPopupsResult | undefined): App.LabModalPopupResult | undefined {
  return getFirstEligibleLabItemByRank(modalPopups?.popups, popup => !isModalPopupSuppressed(popup))
}

export default function ModalPopupsGate() {
  const dispatch = useAppDispatch()
  const tryShowModal = useContext(TryShowModalContext)
  const isModalStackIdle = useContext(ModalStackIdleContext)
  const { currentPath, distinctId, token, labRequestContextKey } = useLabModalPopupsRequestContext()
  const modalPopups = useAppSelector(state => state.system.features.modalPopups)
  const modalPopupsRequestPath = useAppSelector(state => state.system.features.modalPopupsRequestPath)
  const shownPopupIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    dispatch(fetchLabModalPopups(currentPath, distinctId, token))
  }, [dispatch, currentPath, distinctId, token, labRequestContextKey])

  const popup = useMemo(() => {
    if (!isModalStackIdle) {
      return undefined
    }
    if (modalPopupsRequestPath && modalPopupsRequestPath !== currentPath) {
      return undefined
    }
    return getRenderableModalPopup(modalPopups)
  }, [isModalStackIdle, modalPopups, modalPopupsRequestPath, currentPath])

  const handleActionClick = useCallback((_event: React.MouseEvent<HTMLButtonElement>, resolve: (result?: unknown) => void) => {
    resolve()
  }, [])

  useEffect(() => {
    if (!isModalStackIdle || !popup || shownPopupIdsRef.current.has(popup.id)) {
      return
    }

    const content = popup.content
    const secondaryCta = content.primaryCta ? content.secondaryCta : undefined
    const modalStyle = getModalPopupStyle(popup)
    const modalTitle = content.title.trim()
    const modalTagText = content.tag?.text.trim()
    const modalTagVariant = content.tag?.variant
    const modalDescription = content.description?.trim()
    const hasTag = !!(modalTagText && modalTagVariant)
    const hasContent = !!(hasTag || modalTitle || modalDescription)

    const modalPromise = tryShowModal(() => {
      recordModalPopupSeen(popup)

      const modalContent = hasContent ? (
        <ContentStack>
          {hasTag ? <Label kind="primary" variant={modalTagVariant}>{modalTagText}</Label> : null}
          {modalTitle ? <Heading variant="heading2">{modalTitle}</Heading> : null}
          {modalDescription ? <BodyText variant="large" wrap="pre-wrap">{modalDescription}</BodyText> : null}
        </ContentStack>
      ) : null

      return (
        <StyledModal
          className={modalStyle === 'dark' ? LAB_MODAL_POPUP_DARK_CLASS_NAME : undefined}
          title=""
          isHeaderOverlay
          image={content.imageId ? { id: content.imageId, title: modalTitle || popup.name } : undefined}
          imageParams={content.imageId ? { mobileAspectRatio: '2:1', tabletAspectRatio: '2:1', desktopAspectRatio: '2:1' } : undefined}
          primaryActionText={content.primaryCta?.label}
          primaryActionTo={content.primaryCta?.path}
          secondaryActionText={secondaryCta?.label}
          secondaryActionTo={secondaryCta?.path}
          onPrimaryActionClick={handleActionClick}
          onSecondaryActionClick={handleActionClick}
          data-testid="lab-modal-popup"
        >
          {modalContent}
        </StyledModal>
      )
    })

    if (!modalPromise) {
      return
    }

    shownPopupIdsRef.current.add(popup.id)
    modalPromise.then(() => undefined)
  }, [isModalStackIdle, popup, tryShowModal, handleActionClick])

  return null
}
